/**
 * What state a session is in, and where somebody is allowed to be sent.
 *
 * Both are small and both are security decisions, so both live here where they
 * can be tested without a browser.
 */

export interface SessionLike {
    /** Unix seconds, as Supabase reports it. */
    expires_at?: number | null;
    user?: { id: string; email?: string | null; email_confirmed_at?: string | null } | null;
}

export type SessionState = 'signed-out' | 'active' | 'expired';

/**
 * A session about to expire is treated as expired.
 *
 * The client refreshes tokens on its own, but a laptop that has been asleep
 * comes back with a token that is valid for four more seconds, and the first
 * request fails in whatever way that screen happens to handle. Treating the
 * last minute as expired means the person is asked to sign in, once, instead of
 * watching something fail.
 */
const EXPIRY_GRACE_SECONDS = 60;

export function sessionState(
    session: SessionLike | null | undefined,
    nowMs: number = Date.now(),
): SessionState {
    if (!session || !session.user) return 'signed-out';
    if (session.expires_at == null) return 'active';
    const expiresMs = session.expires_at * 1000;
    return expiresMs - EXPIRY_GRACE_SECONDS * 1000 <= nowMs ? 'expired' : 'active';
}

/** Has this person proved the address is theirs? */
export const isVerified = (session: SessionLike | null | undefined): boolean =>
    Boolean(session?.user?.email_confirmed_at);

/* ------------------------------- redirection ------------------------------- */

/**
 * Where we are willing to send somebody after they sign in.
 *
 * `?returnTo=` comes from the address bar, which means it comes from whoever
 * wrote the link. Left unchecked it is an open redirect: a link to our own
 * sign-in page that lands on somebody else's site, wearing our domain in the
 * address bar all the way there. Only a path on this origin is ever honoured,
 * and anything else falls back to the default.
 *
 * `//evil.test` and `/\evil.test` are the two that catch people out: both are
 * protocol-relative and both start with a slash.
 */
export function safeReturnTo(raw: string | null | undefined, fallback = '/'): string {
    const value = (raw ?? '').trim();
    if (!value) return fallback;
    if (!value.startsWith('/')) return fallback;
    // Protocol-relative: "//host" and, because browsers normalise it, "/\host".
    if (/^\/[/\\]/.test(value)) return fallback;
    if (/[ -]/.test(value)) return fallback;
    return value;
}

/**
 * The absolute URL an emailed link should come back to.
 *
 * Built from the app's own configured origin rather than from anything the page
 * was given, so a link in an email can only ever land on this app.
 */
export function callbackUrl(appUrl: string, path: string, returnTo?: string | null): string {
    const base = appUrl.replace(/\/+$/, '');
    const safe = safeReturnTo(returnTo, '');
    const query = safe ? `?returnTo=${encodeURIComponent(safe)}` : '';
    return `${base}${path}${query}`;
}
